import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { FundsService } from 'api';
import { fundsAction } from 'common/actions';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { Fund as StoreFund } from 'common/types/fund';
import type { UseStoreValues } from 'common/types/store';
import type { SecondaryNavigationTitleAccessoryControlConfig } from 'context/FirmBaseLayoutContext';
import type { UseFirmFundFilterReturn } from 'pages/Firms/hooks';
import { useDialogFormsStore } from 'store';
import { useIntelligenceContext } from './IntelligenceContext';

const FILTER_BY_FUND_LABEL = 'Filter by Fund';

export interface FirmSummaryFundFilterResult {
  titleAccessory: SecondaryNavigationTitleAccessoryControlConfig;
  fundMenuAnchor: HTMLElement | null;
  handleCloseFundMenu: () => void;
  fundOptions: UseFirmFundFilterReturn['fundOptions'];
  isAllFundsSelected: boolean;
  selectedFund: string;
  selectedFundId: number | undefined;
  canCreateFund: boolean;
  canEditFund: boolean;
  handleSelectFund: (value: string) => void;
  handleAddFund: () => void;
  handleEditFund: (fundId: number) => Promise<void>;
}

const useFirmSummaryFundFilter = (): FirmSummaryFundFilterResult => {
  const { fundOptions, isAllFundsSelected, isLoadingFunds, selectedFund, selectedFundId, setSelectedFund } =
    useIntelligenceContext();

  const [, dispatch] = useStore() as UseStoreValues;
  const { canCreateFund, canEditFund } = useFirmScopedPermissions();
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const setShouldUpdateFund = useDialogFormsStore(state => state.setShouldUpdateFund);
  const [fundMenuAnchor, setFundMenuAnchor] = useState<HTMLElement | null>(null);

  const handleOpenFundMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setFundMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseFundMenu = useCallback(() => {
    setFundMenuAnchor(null);
  }, []);

  const handleSelectFund = useCallback(
    (fundValue: string) => {
      setSelectedFund(fundValue);
      setFundMenuAnchor(null);
    },
    [setSelectedFund]
  );

  const handleAddFund = useCallback(() => {
    if (!canCreateFund) {
      return;
    }

    setFundMenuAnchor(null);
    setShouldUpdateFund(false);
    setDisplayFundDialogForm(true);
  }, [canCreateFund, setDisplayFundDialogForm, setShouldUpdateFund]);

  const handleEditFund = useCallback(async (fundId: number) => {
    if (!canEditFund) {
      return;
    }

    setFundMenuAnchor(null);
    try {
      const fullFund = await FundsService.apiFundsRead(String(fundId));
      dispatch(fundsAction.setFundInfo(fullFund as unknown as StoreFund));
      setShouldUpdateFund(true);
      setDisplayFundDialogForm(true);
    } catch {
      // menu is already closed — nothing further to do
    }
  }, [canEditFund, dispatch, setDisplayFundDialogForm, setShouldUpdateFund]);

  const canOpenFundMenu = !isLoadingFunds;

  const titleAccessory = useMemo<SecondaryNavigationTitleAccessoryControlConfig>(
    () => ({
      type: 'selector',
      variant: 'filter',
      value: isAllFundsSelected ? FILTER_BY_FUND_LABEL : selectedFund,
      disabled: !canOpenFundMenu,
      onClick: canOpenFundMenu ? handleOpenFundMenu : undefined,
    }),
    [canOpenFundMenu, handleOpenFundMenu, isAllFundsSelected, selectedFund]
  );

  return {
    titleAccessory,
    fundMenuAnchor,
    handleCloseFundMenu,
    fundOptions,
    isAllFundsSelected,
    selectedFund,
    selectedFundId,
    canCreateFund,
    canEditFund,
    handleSelectFund,
    handleAddFund,
    handleEditFund,
  };
};

export default useFirmSummaryFundFilter;
